'use es6';
/*
 * Adds find/replace keyboard commands and UI.
 *
 * !! The following MUST be imported !!
 *   //= require codemirror/static-5.24/addon/scroll/simplescrollbars.css
 *   //= require codemirror/static-5.24/addon/search/matchesonscrollbar.css
 *   //= require react-codemirror/static-1.5611/sass/panel.sass
 *   //= require react-codemirror/static-1.5611/sass/find.sass
 */

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import CodeMirror from 'codemirror';
import 'codemirror/addon/display/panel';
import 'codemirror/addon/scroll/annotatescrollbar';
import 'codemirror/addon/scroll/simplescrollbars';
import 'codemirror/addon/search/matchesonscrollbar';
import 'codemirror/addon/search/searchcursor';
import 'codemirror/addon/search/jump-to-line';
import Position from 'react-codemirror/enum/Position';
import FindReplaceMode from 'react-codemirror/enum/FindReplaceMode';
import Browser from 'react-codemirror/util/Browser';
import * as Find from 'react-codemirror/util/Find';
import { loadCodeMirrorCSS, loadReactCodeMirrorCSS } from 'react-codemirror/util/LoadCSS';
import loadCSS from 'react-codemirror/decorator/_internal/loadCSS';
import Panel from 'react-codemirror/decorator/_internal/components/panel/Panel';
import FindReplace from 'react-codemirror/decorator/_internal/components/find/FindReplace';
import { pick, compose } from '../util/miniUnderscore.js';
const withLoadCSS = loadCSS([loadCodeMirrorCSS('/addon/scroll/simplescrollbars.css'), loadCodeMirrorCSS('/addon/search/matchesonscrollbar.css'), loadReactCodeMirrorCSS('/sass/panel.css'), loadReactCodeMirrorCSS('/sass/find.css')]);
const FIND_STATE_UI_FIELDS = ['queryText', 'replaceText', 'jumpToLineText', 'mode', 'isCaseSensitive', 'isRegex', 'matchCount'];
const FIND_STATE_UI_FIELDS_INDEX = FIND_STATE_UI_FIELDS.reduce((coll, field) => {
  coll[field] = true;
  return coll;
}, {});
const PANEL_DISPLAY_MODES_INDEX = {
  [FindReplaceMode.FIND]: true,
  [FindReplaceMode.REPLACE]: true,
  [FindReplaceMode.JUMP_TO_LINE]: true
};
export default function find() {
  return function withFind(CodemirrorComponent) {
    /**
     * @class
     */
    class WithFind extends Component {
      constructor(props) {
        super(props);
        this.state = {};
        /** @type {CodeMirror} CodeMirror instance. */

        this.codemirror = null;
        /** @type {HTMLElement} Element, containing FindReplace, that is added to CodeMirror. */

        this.panelElement = null;
        /** @type {Panel} CodeMirror Panel object. */

        this.panel = null;
        this.refCodemirror = this.refCodemirror.bind(this);
        this.toggleMode = this.toggleMode.bind(this);
        this.toggleCaseSensitivity = this.toggleCaseSensitivity.bind(this);
        this.toggleRegex = this.toggleRegex.bind(this);
        this.close = this.close.bind(this);
        this.onFindInput = this.onFindInput.bind(this);
        this.onReplaceInput = this.onReplaceInput.bind(this);
        this.findNext = this.findNext.bind(this);
        this.findPrev = this.findPrev.bind(this);
        this.findAll = this.findAll.bind(this);
        this.replaceNext = this.replaceNext.bind(this);
        this.replaceAll = this.replaceAll.bind(this);
        this.jumpToLine = this.jumpToLine.bind(this);
        this.onJumpToLineInput = this.onJumpToLineInput.bind(this);
        this.handlePanelKeyMap = this.handlePanelKeyMap.bind(this);
        this.updateOnFindStateChange = this.updateOnFindStateChange.bind(this);
      }

      componentDidUpdate(prevProps, prevState) {
        if (prevState !== this.state) {
          this.mountFindReplace();
        } // Toggle of panel position prop.


        if (prevProps.findPanelPosition !== this.props.findPanelPosition && PANEL_DISPLAY_MODES_INDEX[this.state.mode]) {
          this.__replacePanel();
        } // Toggle of disableFind prop.


        if (prevProps.disableFind !== this.props.disableFind) {
          if (this.props.disableFind) {
            this.teardown();
          } else {
            this.initialize();
          }
        } // Prop handler to listen to FindState has changed.


        if (prevProps.onFindStateChange !== this.props.onFindStateChange) {
          this.unbindFindStateChange(prevProps.onFindStateChange);

          if (!this.props.disableFind) {
            this.bindFindStateChange(this.props.onFindStateChange);
          }
        }
      }

      componentWillUnmount() {
        this.teardown();
      }

      codemirrorMounted(codemirror) {
        if (this.codemirror === codemirror) {
          return;
        }

        this.codemirror = codemirror;
        this.initialize();
      }

      initialize() {
        if (this.props.disableFind) {
          return;
        }

        if (!this.codemirror) {
          return;
        }

        this.setComponentStateFromFindState();
        this.bindFindStateChange(this.updateOnFindStateChange);
        this.bindFindStateChange(this.props.onFindStateChange);
        this.codemirror.addKeyMap(this.keyMap);

        this.__addPanel();

        this.mountFindReplace();
      }

      teardown() {
        this.unbindFindStateChange(this.updateOnFindStateChange);
        this.unbindFindStateChange(this.props.onFindStateChange);
        this.codemirror.removeKeyMap(this.keyMap);

        this.__unmountFindReplacePanel();

        this.__removePanel();
      }

      bindFindStateChange(fn, unbind = false) {
        if (!(this.codemirror && fn)) {
          return;
        }

        const method = unbind ? 'off' : 'on';
        Find.getFindState(this.codemirror)[method](fn);
      }

      unbindFindStateChange(fn) {
        this.bindFindStateChange(fn, true);
      }

      updateOnFindStateChange(changeObj) {
        const {
          field
        } = changeObj;

        if (!FIND_STATE_UI_FIELDS_INDEX[field]) {
          return;
        }

        const {
          value
        } = changeObj;

        if (this.state[field] === value) {
          return;
        }

        this.setState({
          [field]: value
        });
      }

      setComponentStateFromFindState() {
        if (!this.codemirror) {
          return;
        }

        const findState = Find.getFindState(this.codemirror);
        const fields = pick(findState, FIND_STATE_UI_FIELDS);
        this.setState(Object.assign({}, fields));
      }

      onFindInput(queryText) {
        Find.getFindState(this.codemirror).queryText = queryText || '';
        Find.find(this.codemirror);
      }

      onReplaceInput(replaceText) {
        Find.getFindState(this.codemirror).replaceText = replaceText || '';
      }

      onJumpToLineInput(jumpToLineText) {
        // TODO: Validator
        Find.getFindState(this.codemirror).jumpToLineText = jumpToLineText || '';
      }
      /*
       * Keyboard & Event bindings
       */


      toggleMode() {
        const findState = Find.getFindState(this.codemirror);

        switch (findState.mode) {
          case FindReplaceMode.FIND:
            findState.mode = FindReplaceMode.REPLACE;
            break;

          case FindReplaceMode.REPLACE:
          default:
            findState.mode = FindReplaceMode.FIND;
            break;
        }

        Find.focusFindInput(this.codemirror);
      }

      toggleCaseSensitivity() {
        this.codemirror.execCommand('hsToggleFindCase');
      }

      toggleRegex() {
        this.codemirror.execCommand('hsToggleFindRegex');
      }

      close() {
        this.codemirror.execCommand('hsClearFind');
      }

      findNext() {
        this.codemirror.execCommand('hsFindNext');
      }

      findPrev() {
        this.codemirror.execCommand('hsFindPrev');
      }

      findAll() {
        this.codemirror.execCommand('hsFindAll');
      }

      replaceNext() {
        this.codemirror.execCommand('hsReplaceNext');
      }

      replaceAll() {
        this.codemirror.execCommand('hsReplaceAll');
      }

      jumpToLine() {
        this.codemirror.execCommand('hsExecuteJumpToLine');
      }
      /**
       * Allows panel element to respond to the keymaps.
       */


      handlePanelKeyMap(evt) {
        const key = CodeMirror.keyName(evt);

        if (!key) {
          return;
        }

        const command = this.keyMap[key];

        if (!command) {
          return;
        }

        CodeMirror.e_stop(evt);

        if (typeof command === 'string') {
          this.codemirror.execCommand(command);
        } else {
          command(this.codemirror);
        }
      }

      get keyMap() {
        if (this.__keyMap) {
          return this.__keyMap;
        }

        const {
          isMac
        } = Browser;
        this.__keyMap = CodeMirror.normalizeKeyMap({
          Esc: 'hsClearFind',
          'Alt-G': 'hsJumpToLine',
          [isMac ? 'Cmd-F' : 'Ctrl-F']: 'hsFind',
          [isMac ? 'Shift-Cmd-F' : 'Shift-Ctrl-F']: 'hsReplace',
          [isMac ? 'Cmd-G' : 'Ctrl-G']: 'hsFindNext',
          [isMac ? 'Shift-Cmd-G' : 'Shift-Ctrl-G']: 'hsFindPrev'
        });
        return this.__keyMap;
      }

      refCodemirror(ref) {
        const codemirror = ref && ref.codemirror;
        this.codemirrorMounted(codemirror);

        if (this.props.refCodemirror) {
          this.props.refCodemirror(ref);
        }
      }

      __addPanel(replace = false) {
        if (this.panel && replace === false) {
          return;
        }

        if (!this.codemirror) {
          return;
        }

        if (!this.panelElement) {
          this.panelElement = document.createElement('div');
          this.panelElement.addEventListener('keydown', this.handlePanelKeyMap);
        }

        const options = {};

        if (this.panel && replace) {
          this.panel.clear();
          options.replace = this.panel;
        }

        this.panel = this.codemirror.addPanel(this.panelElement, Object.assign({}, options, {
          position: this.props.findPanelPosition,
          stable: true
        }));
      }

      __replacePanel() {
        this.__addPanel(true);
      }

      __removePanel() {
        if (this.panel) {
          this.panel.clear();
          this.panel = null;
        }

        if (this.panelElement) {
          this.panelElement.removeEventListener('keydown', this.handlePanelKeyMap);
          this.panelElement = null;
        }
      }

      __unmountFindReplacePanel() {
        if (!this.panelElement) {
          return;
        }

        ReactDOM.unmountComponentAtNode(this.panelElement);
      }

      __mountFindReplacePanel() {
        if (!this.codemirror) {
          return;
        }

        ReactDOM.render( /*#__PURE__*/_jsx(Panel, {
          codemirror: this.codemirror,
          position: this.props.findPanelPosition,
          children: /*#__PURE__*/_jsx(FindReplace, {
            mode: this.state.mode,
            isRegex: this.state.isRegex,
            isCaseSensitive: this.state.isCaseSensitive,
            queryText: this.state.queryText,
            replaceText: this.state.replaceText,
            onToggleMode: this.toggleMode,
            onToggleCaseSensitivity: this.toggleCaseSensitivity,
            onToggleRegex: this.toggleRegex,
            onClose: this.close,
            onFindInput: this.onFindInput,
            onReplaceInput: this.onReplaceInput,
            onFindNext: this.findNext,
            onFindAll: this.findAll,
            onFindPrevious: this.findPrev,
            onReplaceNext: this.replaceNext,
            onReplaceAll: this.replaceAll,
            jumpToLineText: this.state.jumpToLineText,
            onJumpToLineInput: this.onJumpToLineInput,
            onJumpToLine: this.jumpToLine
          })
        }), this.panelElement);
      }

      mountFindReplace() {
        if (PANEL_DISPLAY_MODES_INDEX[this.state.mode]) {
          this.__mountFindReplacePanel();
        } else {
          this.__unmountFindReplacePanel();
        }
      }

      render() {
        return /*#__PURE__*/_jsx(CodemirrorComponent, Object.assign({}, this.props, {
          refCodemirror: this.refCodemirror
        }));
      }

    }
    /*
     * Statics
     */


    WithFind.propTypes = {
      refCodemirror: PropTypes.func,
      findPanelPosition: PropTypes.oneOf([Position.BOTTOM, Position.TOP]),
      disableFind: PropTypes.bool,
      onFindStateChange: PropTypes.func
    };
    WithFind.defaultProps = {
      findPanelPosition: Position.BOTTOM,
      disableFind: false
    };
    WithFind.displayName = `WithFind(${CodemirrorComponent.displayName || CodemirrorComponent.name || 'CodemirrorComponent'})`;
    return compose(withLoadCSS)(WithFind);
  };
}